import React from 'react';
import { APP_URLS } from '../../utils/appUrls';
import { LAST_YEAR } from '../../utils/helpers';

/** CHAPTER 1: Veteran Information */
export const ContactInfoDescription = (
  <>
    <p>
      Adding your email and phone number is optional. But this information helps
      us contact you faster if we need to follow up with you about your
      application. If you don’t add this information, we’ll use your address to
      contact you by mail.
    </p>
    <p className="vads-u-margin-bottom--4">
      <strong>Note:</strong> We’ll always mail you a copy of our decision on
      your application for your records.
    </p>
  </>
);

/** CHAPTER 2: VA Benefits */
export const PensionDescription = (
  <div className="vads-u-margin-top--2 vads-u-margin-bottom--4">
    <va-details label="Why we ask for this information">
      <p>We use this information to help us decide these 3 things:</p>
      <ul>
        <li>
          What types of VA healthcare benefits you’re eligible for,{' '}
          <strong>and</strong>
        </li>
        <li>
          How soon we enroll you in VA healthcare, <strong>and</strong>
        </li>
        <li>
          How much (if anything) you’ll have to pay toward the cost of your care
        </li>
      </ul>
      <p>
        If you have a Veterans Pension, you may pay a lower copay, or no copay,
        for certain types of care and services.
      </p>
    </va-details>
  </div>
);

/** CHAPTER 4: Household Information */
export const DependentSupportDescription = (
  <>
    <p>
      We consider any payments, even if they aren’t regular or the same amount,
      to be financial support.
    </p>
    <p>
      <strong>
        Financial support includes payments for these types of expenses:
      </strong>
    </p>
    <ul>
      <li>Tuition or medical bills</li>
      <li>Monthly child support</li>
      <li>One-time payment financial support</li>
    </ul>
  </>
);

export const EducationalExpensesDescription = (
  <div className="vads-u-margin-top--2 vads-u-margin-bottom--4">
    <va-details label="What we consider college or vocational expenses">
      <p>
        College and vocational expenses include payments for these expenses
        related to your own education:
      </p>
      <ul>
        <li>Tuition</li>
        <li>Books</li>
        <li>Fees</li>
        <li>Course materials</li>
      </ul>
      <p>
        Only include expenses for your own education (not your dependents’
        education).
      </p>
    </va-details>
  </div>
);

export const GrossIncomeDescription = (
  <>
    <div className="vads-u-margin-y--1">
      Gross income is income before taxes and any other deductions are
      subtracted.
    </div>
    <div className="vads-u-margin-bottom--4">
      <va-details label="What we consider gross annual income">
        <p>Gross income includes these types of income from a job:</p>
        <ul>
          <li>Wages</li>
          <li>Bonuses</li>
          <li>Tips</li>
          <li>Severance pay</li>
        </ul>
      </va-details>
    </div>
  </>
);

export const MedicalExpensesDescription = (
  <div className="vads-u-margin-top--2 vads-u-margin-bottom--4">
    <va-details label="What we consider non-reimbursable medical expenses">
      <p>
        Non-reimbursable medical expenses include costs you or your spouse paid
        for these types of healthcare for yourselves, your dependents, or others
        you have the moral obligation to support:
      </p>
      <ul>
        <li>Doctor or dentist appointments</li>
        <li>Medications</li>
        <li>Medicare or health insurance</li>
        <li>Inpatient hospital care</li>
        <li>Nursing home care</li>
      </ul>
      <p>
        We only consider expenses non-reimbursable if your health insurance
        doesn’t pay you back for the cost.
      </p>
    </va-details>
  </div>
);

export const OtherIncomeDescription = (
  <>
    <div className="vads-u-margin-y--1">
      Other income is additional income that doesn’t come from a job.
    </div>
    <div className="vads-u-margin-bottom--4">
      <va-details label="What we consider other annual income">
        <p>Other income includes things like this:</p>
        <ul>
          <li>Retirement benefits</li>
          <li>Unemployment</li>
          <li>VA benefit compensation</li>
          <li>Money from the sale of a house</li>
          <li>Interest from investments</li>
        </ul>
      </va-details>
    </div>
  </>
);

export const SpouseAdditionalInformationDescription = (
  <>
    <p>
      Fill this out to the best of your knowledge. The more accurate your
      responses, the faster we can process your application.
    </p>
    <p>
      This information helps us determine if your spouse was your dependent in{' '}
      {LAST_YEAR}.
    </p>
  </>
);

export const SpouseFinancialSupportDescription = (
  <>
    <p>
      We consider any payments, even if they aren’t regular or the same amount,
      to be financial support.
    </p>
    <p>
      <strong>Financial support includes these types of payments:</strong>
    </p>
    <ul>
      <li>Monthly spousal support</li>
      <li>One-time payment financial support</li>
    </ul>
  </>
);

/** CHAPTER 5: Insurance Information */
export const FacilityLocatorDescription = (
  <div className="vads-u-margin-y--4">
    <va-details label="More information about locations and medical care">
      <p>
        <va-link
          href={APP_URLS.facilities}
          text="Find locations with the VA Facility Locator"
          external
        />
      </p>
      <p>
        If you’re looking for medical care outside the continental U.S. or Guam,
        you’ll need to sign up for our Foreign Medical Program.{' '}
        <va-link
          href="/health-care/foreign-medical-program/"
          text="Learn more about the Foreign Medical Program"
          external
        />
      </p>
      <p>
        You can also visit{' '}
        <va-link
          href="https://www.benefits.va.gov/PERSONA/veteran-abroad.asp"
          text="Veterans Living Abroad"
          external
        />
        .
      </p>
    </va-details>
  </div>
);

export const MedicaidDescription = (
  <>
    <p>
      Medicaid is a federal health insurance program for adults and families
      with low income levels and people with disabilities.
    </p>
    <p>
      <strong>Note:</strong> Some states use different names for their Medicaid
      programs.
    </p>
  </>
);

export const MedicareLearnMoreDescription = (
  <va-link
    href="/resources/va-health-care-and-other-insurance/"
    text="Learn more about how VA healthcare works with Medicare and other insurance"
    external
  />
);

export const PolicyOrGroupTitle = (
  <>
    <span className="vads-u-font-size--h3">
      Insurance policy number or group code
    </span>
    <span className="schemaform-required-span vads-u-font-family--sans vads-u-font-size--base vads-u-font-weight--normal">
      (*Required)
    </span>
  </>
);

export const TricarePolicyDescription = (
  <div className="vads-u-margin-y--2">
    <va-details label="I have TRICARE. What’s my policy number?">
      <p>
        You can use your Department of Defense benefits number (DBN) or your
        Social Security number as your policy number.
      </p>
      <p>
        Your DBN is an 11-digit number. You’ll find this number on the back of
        your military ID card.
      </p>
    </va-details>
  </div>
);
